import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoBoyutOlcusu } from './FikoBoyutOlcusu';
import * as THREE from 'three';

/**
 * 📏 CATIA-style Dimension Annotation Dialog
 */
@Component({
  selector: 'app-fiko-boyut-olcusu',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-boyut-olcusu.component.html',
  styleUrls: ['./fiko-boyut-olcusu.component.css']
})
export class FikoBoyutOlcusuComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: FikoThreeJsComponent;
  @Input() pencere: any;

  // Dimension settings
  renk: string = '#00FFFF';
  birim: string = 'mm';
  offset: number = 0.5;
  okBoyutu: number = 0.2;
  boyut: number = 12;

  // Point selection
  baslangicNokta: THREE.Vector3 | null = null;
  bitisNokta: THREE.Vector3 | null = null;
  noktaSecimModu: 'baslangic' | 'bitis' | null = null;

  // Current annotation
  mevcutBoyutOlcusu: FikoBoyutOlcusu | null = null;

  // Predefined colors
  renkler = ['#00FFFF', '#FFD700', '#FF4444', '#00FF00', '#FF00FF', '#FFA500'];

  // Sabit boyut - kamera zoom'una göre scale ayarlanır
  sabitBoyut: boolean = true;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Accordion states
  olcuAyarlariAcik = true;
  gorunumAyarlariAcik = false;

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.noktaSecimModu) {
      this.noktaSecimModunuKapat();
    }
  }

  /**
   * Start point selection mode
   */
  baslangicNoktasiSec(): void {
    this.noktaSecimModu = 'baslangic';
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Start end point selection mode
   */
  bitisNoktasiSec(): void {
    this.noktaSecimModu = 'bitis';
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Handle point click
   */
  private noktaTiklama = (event: MouseEvent) => {
    const position = this.get3DPositionFromMouse(event);
    if (!position) return;

    if (this.noktaSecimModu === 'baslangic') {
      this.baslangicNokta = position;
    } else if (this.noktaSecimModu === 'bitis') {
      this.bitisNokta = position;
    }

    this.noktaSecimModunuKapat();

    // Eğer her iki nokta da seçildiyse önizleme göster
    if (this.baslangicNokta && this.bitisNokta) {
      this.onizlemeGuncelle();
    }
  };

  /**
   * Close point selection mode
   */
  private noktaSecimModunuKapat(): void {
    this.noktaSecimModu = null;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
      canvas.removeEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Get 3D position from mouse
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    const meshes = sahne.children.filter((obj: any) => obj.isMesh || obj.type === 'Mesh');
    const intersects = raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      return intersects[0].point.clone();
    }

    // Fallback
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.aktifKamera.getWorldDirection(direction);
    const position = this.anaGosterim.aktifKamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  /**
   * Toggle sabit boyut (fixed size regardless of zoom)
   */
  sabitBoyutDegistir(): void {
    if (this.mevcutBoyutOlcusu) {
      this.mevcutBoyutOlcusu.setSabitBoyut(this.sabitBoyut);
      this.anaGosterim?.ciz();
    }
  }

  /**
   * Update preview
   */
  onizlemeGuncelle(): void {
    if (!this.baslangicNokta || !this.bitisNokta) return;

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) return;

    // Eski önizleme varsa sil
    if (this.mevcutBoyutOlcusu) {
      sahne.remove(this.mevcutBoyutOlcusu);
      this.mevcutBoyutOlcusu.dispose();
    }

    // Yeni önizleme oluştur
    this.mevcutBoyutOlcusu = new FikoBoyutOlcusu(
      sahne,
      this.baslangicNokta,
      this.bitisNokta,
      {
        renk: this.renk,
        birim: this.birim,
        offset: this.offset,
        okBoyutu: this.okBoyutu,
        boyut: this.boyut
      }
    );

    sahne.add(this.mevcutBoyutOlcusu);
    this.anaGosterim?.ciz();
  }

  /**
   * Add dimension to scene
   */
  ekle(): void {
    if (!this.baslangicNokta || !this.bitisNokta) {
      alert('Lütfen önce başlangıç ve bitiş noktalarını seçin!');
      return;
    }

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) return;

    // Create dimension
    const boyutOlcusu = new FikoBoyutOlcusu(
      sahne,
      this.baslangicNokta,
      this.bitisNokta,
      {
        renk: this.renk,
        birim: this.birim,
        offset: this.offset,
        okBoyutu: this.okBoyutu,
        boyut: this.boyut
      }
    );

    // Add to etiketler group
    const etiket = sahne.fikoEtiketler.ekleBoyutOlcusu();
    etiket.userData['FikoAnnotation'] = boyutOlcusu;
    boyutOlcusu.parent = etiket;
    sahne.add(boyutOlcusu);

    this.anaGosterim?.ciz();
    this.anaGosterim?.FikoYT?.degisti?.emit();

    // Reset
    this.baslangicNokta = null;
    this.bitisNokta = null;
    if (this.mevcutBoyutOlcusu) {
      sahne.remove(this.mevcutBoyutOlcusu);
      this.mevcutBoyutOlcusu = null;
    }
  }

  /**
   * Close dialog
   */
  kapat(): void {
    if (this.mevcutBoyutOlcusu) {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (sahne) {
        sahne.remove(this.mevcutBoyutOlcusu);
        this.mevcutBoyutOlcusu.dispose();
      }
    }
    // pencere.instance → FikoDialogCore instance
    const dialogInstance = this.pencere?.instance ?? this.pencere;
    if (dialogInstance?.kapa) {
      dialogInstance.kapa();
    } else if (dialogInstance?.kapat) {
      dialogInstance.kapat();
    }
  }
}
